import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { commentsApi, paymentsApi, formatCurrency } from '../utils/api';
import { PaymentMethodIcon } from '../pages/SupplierManagementPage';
import { useIsMobile } from '../utils/useIsMobile';
import { formatIsoDayShort } from '../utils/dateFormat';
import Icon from './Icon';
import { useSheetDrag } from '../utils/useSheetDrag';

// Everything known about one document's payment, opened from its שולם badge.
//
// This exists because the badge used to carry its whole story in a `title=`
// tooltip: the amount, the method and the date were all technically present and
// practically invisible — tooltips don't fire on touch at all, and on a desktop
// nobody hovers a badge they don't already suspect is hiding something.
//
// Desktop renders a popover anchored to the badge; mobile renders a bottom
// sheet. Same content, and the trigger is a real <button> either way, so the
// panel is reachable by keyboard and by thumb rather than by hover.
//
// TWO KINDS OF NOTE live here, deliberately kept apart:
//   • the payment REASON (payments.notes) — system-captured, and mandatory
//     whenever the amount differs from the document (server/lib/paymentAmount.js).
//     One per payment, quoted, attributed to whoever recorded it.
//   • document COMMENTS (the comments table) — a conversation, many authors,
//     added by hand.
// They are both "comments" in conversation, which is exactly why showing them
// in one undifferentiated list would be worse than showing neither.

const fmtPaymentDate = (iso) => (iso ? formatIsoDayShort(iso) : '');

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(String(dateStr).replace(' ', 'T') + 'Z');
  const mins = Math.floor((Date.now() - date) / 60000);
  if (Number.isNaN(mins)) return '';
  if (mins < 1) return 'עכשיו';
  if (mins < 60) return `לפני ${mins} דק׳`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `לפני ${h} ש׳`;
  const d = Math.floor(h / 24);
  if (d < 30) return `לפני ${d} י׳`;
  return `לפני ${Math.floor(d / 30)} ח׳`;
};

// Muted styling for the two non-person authors ("לא תועד", auto-pay). Mirrors
// PaymentHistoryModal's MUTED_KINDS — the server decides the LABEL, the client
// only decides how it looks.
const MUTED_KINDS = new Set(['unknown', 'system']);

// Desktop popover width. Matches .payment-popover's own box so the clamp math
// and the CSS can't drift.
const POPOVER_W = 320;

export default function PaymentDetailPanel({ doc, onClose, anchorRect }) {
  const isMobile = useIsMobile();
  const [comments, setComments] = useState(null);   // null = still loading
  const [payments, setPayments] = useState(null);   // every payment on this doc, oldest first
  const panelRef = useRef(null);
  // On a phone this is a bottom sheet: swipe down closes it, as its grabber promises.
  useSheetDrag(panelRef, onClose, { enabled: isMobile });
  // Phone sheet: lock the page behind it, like FilterSheet.
  useEffect(() => {
    if (!isMobile) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isMobile]);
  // Desktop only. Starts null so the first paint is off-screen rather than in
  // the wrong place — the panel's height isn't knowable until it has rendered
  // (the comment thread loads in), and guessing it produces a visible jump.
  const [pos, setPos] = useState(null);

  useEffect(() => {
    let alive = true;
    commentsApi.getAll('document', doc.id)
      .then(r => { if (alive) setComments(r.data); })
      .catch(() => { if (alive) setComments([]); });   // a failed thread must not blank the payment facts above it
    // A document can carry several payments now (a down-payment and its
    // balance), so the row's own "latest payment" fields can't tell the story —
    // they would show the balance and hide what it was a balance of.
    paymentsApi.getByDocument(doc.id)
      .then(r => { if (alive) setPayments(r.data); })
      .catch(() => { if (alive) setPayments([]); });
    return () => { alive = false; };
  }, [doc.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) onClose(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  // Place the desktop popover only after it has been measured. Below the badge
  // when there is room, flipped above it when there isn't — these rows sit low
  // in a long table, so "always below" put the panel off the bottom of the
  // screen exactly on the rows people click most. Horizontally clamped to the
  // viewport because the badge lives in a wide, horizontally-scrolling table
  // and its rect can sit near either edge.
  useLayoutEffect(() => {
    if (isMobile || !anchorRect || !panelRef.current) return;
    const h = panelRef.current.offsetHeight;
    const vh = window.innerHeight;
    const below = anchorRect.bottom + 6;
    const fitsBelow = below + h <= vh - 8;
    const top = fitsBelow
      ? below
      : Math.max(8, Math.min(anchorRect.top - h - 6, vh - h - 8));
    const left = Math.min(
      Math.max(8, anchorRect.left + anchorRect.width / 2 - POPOVER_W / 2),
      window.innerWidth - POPOVER_W - 8,
    );
    setPos({ top, left });
    // `comments` is a dependency on purpose: the thread arrives after the first
    // paint and changes the height, which can flip a panel that only just fit.
  }, [isMobile, anchorRect, comments, payments]);

  const total     = Math.abs(Number(doc.total || 0));
  const paid      = Number(doc.payment_paid_total || 0);
  const diff      = paid - total;
  const partial   = !doc.payment_fully_paid;
  const over      = diff > 0.005;
  const accent    = partial || over ? '#b45309' : '#15803d';
  const many      = Array.isArray(payments) && payments.length > 1;

  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <span style={{ fontWeight: 700, color: accent, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name={partial || over ? 'alert' : 'check'} size={15} />
          {partial ? 'שולם חלקית' : over ? 'שולם ביתר' : 'שולם'}
        </span>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="סגור">
          <Icon name="close" size={16} />
        </button>
      </div>

      {/* The money, biggest thing in the panel — it is the question that opened it. */}
      <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
        {formatCurrency(paid)}
      </div>
      <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 2 }}>
        מתוך {formatCurrency(total)}
      </div>
      {(partial || over) && (
        // The actionable line: what's still owed (or what was overpaid). Stated
        // as a number rather than left for the reader to subtract.
        <div style={{
          marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: '0.87rem', fontWeight: 600,
          background: over ? '#fef3c7' : '#f3f4f6', color: over ? '#b45309' : '#374151',
        }}>
          {partial ? `נותרו לתשלום ${formatCurrency(Number(doc.payment_remaining || 0))}` : `שולם מעבר לסכום המסמך ב-${formatCurrency(diff)}`}
        </div>
      )}

      {/* Every payment on the document, oldest first — they read as a sequence
          ("₪2,000 in August, the ₪1,200 balance in September"). Each carries its
          own reason and its own author, because they can be different people on
          different days; collapsing them into one summary line is what made the
          down-payment invisible in the first place. */}
      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #eef1f5' }}>
        {many && (
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8a94a3', marginBottom: 8 }}>
            תשלומים ({payments.length})
          </div>
        )}
        {payments === null ? (
          <div style={{ fontSize: '0.8rem', color: '#bbb' }}>טוען תשלומים…</div>
        ) : payments.length === 0 ? (
          <div style={{ fontSize: '0.8rem', color: '#bbb' }}>אין תשלומים רשומים</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {payments.map(pmt => (
              <div key={pmt.id} style={many ? { borderInlineStart: '2px solid #e6ecf3', paddingInlineStart: 10 } : undefined}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
                  {/* The per-payment figure only when there are several — on a
                      single payment it would just restate the number above. */}
                  {many && (
                    <span style={{ fontWeight: 700, color: '#1a1a2e' }}>
                      {formatCurrency(pmt.amount != null ? pmt.amount : total)}
                    </span>
                  )}
                  <span>{fmtPaymentDate(pmt.payment_date)}</span>
                  {pmt.payment_method && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <PaymentMethodIcon method={pmt.payment_method} size={14} /> {pmt.payment_method}
                    </span>
                  )}
                </div>
                {/* Labelled, not just quoted. The same note also appears in
                    the comments modal beside הוסף הערה ones, and both views
                    have to name its origin the same way or the two disagree
                    about what the reader is looking at. */}
                {pmt.notes && (
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 12px', margin: '6px 0' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#b45309', marginBottom: 3, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Icon name="card" size={12} /> הערת תשלום
                    </div>
                    <div style={{ fontSize: '0.88rem', color: '#333', lineHeight: 1.55 }}>"{pmt.notes}"</div>
                  </div>
                )}
                <div style={{ fontSize: '0.78rem', color: '#8a94a3', marginTop: 4 }}>
                  נרשם ע״י{' '}
                  <span style={{
                    color: MUTED_KINDS.has(pmt.recorded_by_kind) ? '#bbb' : 'var(--ink-muted)',
                    fontStyle: MUTED_KINDS.has(pmt.recorded_by_kind) ? 'italic' : 'normal',
                  }}>{pmt.recorded_by_label}</span>
                  {pmt.recorded_at_label && ` · ${pmt.recorded_at_label}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document comments — a separate thread, separately labelled and
          separately attributed. Hidden entirely when empty rather than showing
          an empty-state inside a panel that is already about something else. */}
      {comments === null ? (
        <div style={{ fontSize: '0.8rem', color: '#bbb', marginTop: 14, paddingTop: 12, borderTop: '1px solid #eef1f5' }}>טוען הערות…</div>
      ) : comments.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #eef1f5' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8a94a3', marginBottom: 8 }}>
            הערות על המסמך ({comments.length}) · הוסף הערה
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 168, overflowY: 'auto' }}>
            {comments.map(c => (
              <div key={c.id} style={{ background: '#f7f9fc', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.78rem', color: '#0f3460' }}>{c.username}</span>
                  <span style={{ fontSize: '0.72rem', color: '#bbb', whiteSpace: 'nowrap' }}>{timeAgo(c.created_at)}</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#333', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{c.comment}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return createPortal(
      <div className="payment-sheet-overlay">
        <div className="payment-sheet" ref={panelRef} role="dialog" aria-label="פרטי תשלום">
          <div className="payment-sheet-grabber" aria-hidden="true" />
          {body}
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="פרטי תשלום"
      className="payment-popover"
      style={{
        position: 'fixed',
        width: POPOVER_W,
        top: pos ? pos.top : 0,
        left: pos ? pos.left : 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {body}
    </div>,
    document.body,
  );
}
